import { Box, Button, Text, Image } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from './Navbar';
import { CartItemObject, WishlistItemObject } from '../../interfaces/components';
import TopOverlay from '../../assets/images/TopOverlay.webp';
import React from 'react';
import { CloseIcon } from '@chakra-ui/icons';
import { useGlobalStore } from '../../store/global';
import CartDrawer from './Cart';
import useViewportHeight from '../../utils/useViewPortHeight';
import {
  dispatchChangeShopToAnalytics,
  dispatchShareToAnalytics,
  dispatchViewCartToAnalytics,
  Source,
} from '../../analytics';
import WishlistDrawer from './Wishlist';
import { handleCopy } from '../../utils';
import { useCustomToast } from './CustomToast';
import WalmartRealmAnimationMobile from '../WalmartRealmAnimationMobile';
import WalmartRealmAnimationTablet from '../WalmartRealmAnimationTablet';
import WalmartRealmAnimationDesktop from '../WalmartRealmAnimationDesktop';
import { shareWishListShareAnalytics } from '../../utils/wishlist';
import WalmartRealmAnimationSmallerDesktop from '../WalmartRealmAnimationSmallerDesktop';
import WalmartRealmAnimationBiggerDesktop from '../WalmartRealmAnimationBiggerDesktop';
import TikTokPixel from './TikTokPixel';
import MatomoTrackingScript from './Matomo';
import GTM from './GTM';
import FacebookPixel from './FacebookPixel';
import { useShopStore } from '../../store/useShopStore';
import { removeSpecialChars } from '../../utils/storeUtils';
import useWindowDimensions from '../../utils/useWindowDimensions';

interface OverlayProps {
  children: React.ReactNode;
  setShowSite: (state: boolean) => void;
}

function Overlay({ children, setShowSite }: OverlayProps) {
  useViewportHeight();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const location = useLocation();
  const [uiLoaded, setUiLoaded] = useState(false);
  const { height } = useWindowDimensions();
  const [showSplashScreen, setShowSplashScreen] = useState(true);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined = undefined;

    const fetchRegion = async (regionAllowed: boolean) => {
      if (regionAllowed || process.env.NODE_ENV === 'development') {
        setShowSplashScreen(false);
        setShowSite(true);
        setUiLoaded(true);
      } else {
        setShowSplashScreen(false);
        setShowSite(false);
        setUiLoaded(false);

        // Clear local storage
        localStorage.clear();

        // const hasCookies = document.cookie.length > 0;

        // Clear all cookies
        document.cookie.split(';').forEach((cookie) => {
          const [name] = cookie.split('=');
          // Domain is required to ensure cookies are cleared from the correct domain scope
          // Without domain, cookies may only be cleared from the current subdomain
          document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=walmartrealm.com`;
        });
        // Clear third-party cookies by specifying their domains
        document.cookie = `_ttp=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=.tiktok.com`;
        document.cookie = `wm_phase_four_seen=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=walmartrealm.com`;
        document.cookie = `wm_phase_four_seen=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;

        // hasCookies && window.location.reload();

        // redirect to walmart
        setTimeout(() => {
          window.open('https://www.walmart.com/', '_self');
        }, 5000);
      }
    };

    // const regionAllowed = !['', ''].includes(window.region);
    const regionAllowed = !['MX'].includes(window.region);
    console.log('window.region', window.region);
    console.log('regionAllowed', regionAllowed);
    const hasCookies = document.cookie.length > 0;

    if (hasCookies && !regionAllowed) {
      fetchRegion(regionAllowed);
    } else {
      timeoutId = setTimeout(() => {
        fetchRegion(regionAllowed);
      }, 900);
    }

    return () => clearTimeout(timeoutId); // Cleanup timeout
  }, []);

  const {
    bannerClosed,
    setBannerClosed,
    cartActive,
    setCartActive,
    wishlistActive,
    setWishlistActive,
    wishlistItems,
    setWishlistItems,
    cartItems,
    setCartItems,
  } = useGlobalStore();
  const { addInteraction, getNextStore, getLastShop, getStoreById } = useShopStore();
  // const [arViewerActive, setArViewerActive] = useState<boolean>(false);

  useEffect(() => {
    const cart = localStorage.getItem('app_wm_cart');
    if (cart !== null) {
      setCartItems(JSON.parse(cart) as CartItemObject[]);
    }
  }, []);

  useEffect(() => {
    if (!cartActive) return;
    dispatchViewCartToAnalytics({
      ecommerce: { value: 0, currency: 'USD', items: [] },
      product_source: Source.direct,
    });
  }, [cartActive]);

  const updateParentCart = (cart: CartItemObject[]) => {
    localStorage.setItem('app_wm_cart', JSON.stringify(cart));
    setCartItems(cart);
  };

  const updateParentIframe = (wishlist: WishlistItemObject[]) => {
    localStorage.setItem('app_wm_wishlist', JSON.stringify(wishlist));
    setWishlistItems(wishlist);
  };

  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      if (event.data.type === 'nextShop') {
        const nextShop = getNextStore();
        const stringUserName = removeSpecialChars(nextShop.influencer);
        const stringStoreTitle = nextShop?.meta?.slug || removeSpecialChars(nextShop.storeTitle);
        nextShop && navigate(`/viewer/${stringUserName}/${stringStoreTitle}`);
        dispatchChangeShopToAnalytics({
          change_shop_medium: 'next_shop',
          change_shop_target: stringStoreTitle || '',
          store: stringStoreTitle || '',
        });
      } else if (event.data.type === 'lastShop') {
        const removedStoreId = event.data.payload?.removedStoreId;
        const storeId = event.data.payload?.storeId;
        const lastShop = getLastShop(removedStoreId, storeId);
        const stringUserName = removeSpecialChars(lastShop?.influencer || '');
        const stringStoreTitle = lastShop?.meta?.slug || removeSpecialChars(lastShop?.storeTitle || '');
        lastShop && navigate(`/viewer/${stringUserName}/${stringStoreTitle}`);
        dispatchChangeShopToAnalytics({
          change_shop_medium: 'last_shop',
          change_shop_target: stringStoreTitle || '',
          store: stringStoreTitle || '',
        });
      } else if (event.data.type === 'gotoSecretShop') {
        const secretShop = getStoreById('SUMX0CkU1747773069965');
        const stringUserName = removeSpecialChars(secretShop?.influencer || '');
        const stringStoreTitle = secretShop?.meta?.slug || removeSpecialChars(secretShop?.storeTitle || '');
        secretShop && navigate(`/viewer/${stringUserName}/${stringStoreTitle}`);
        dispatchChangeShopToAnalytics({
          change_shop_medium: 'secret_shop',
          change_shop_target: stringStoreTitle || '',
          store: stringStoreTitle || '',
        });
      } else if (event.data.type === 'gotoShop') {
        const data = event.data.payload;
        const stringUserName = removeSpecialChars(data?.influencer || '');
        const stringStoreTitle = data?.meta?.slug || removeSpecialChars(data?.storeTitle || '');
        data && navigate(`/viewer/${stringUserName}/${stringStoreTitle}`);
      } else if (event.data.type == 'updateCart') {
        updateParentCart(event.data.payload);
      } else if (event.data.type == 'updateWishlist') {
        updateParentIframe(event.data.payload);
      } else if (event.data.type === 'addInteraction') {
        addInteraction(event?.data?.payload || '', event?.data?.interaction, event?.data?.tags);
      } else if (event.data === 'enterShops') {
        navigate(`/feed`);
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, []);

  const handleShareWishlist = async () => {
    const domain = `${process.env.REACT_APP_DOMAIN}/#/`;

    const randomStore = getNextStore();
    const stringUserName = removeSpecialChars(randomStore?.influencer || '');
    const stringStoreTitle = randomStore?.meta?.slug || removeSpecialChars(randomStore?.storeTitle || '');
    const sharedPIds = wishlistItems.map((wishlistItem: WishlistItemObject) => wishlistItem.id);
    const sharedPIdsString = sharedPIds.join(',');

    const url = domain + `viewer/${stringUserName}/${stringStoreTitle}?sharedPIds=${sharedPIdsString}`;

    dispatchShareToAnalytics('wishlist', sharedPIdsString, url);
    shareWishListShareAnalytics();

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out my wishlist on Walmart Realm',
          text: 'Take a look at the great items I found on Walmart Realm:',
          url: url,
        });
      } catch (error) {
        console.error('Error sharing content:', error);
      }
    } else {
      handleCopy(url);
      showToast({
        id: 'linkCopiedWishlist',
        title: 'Link Copied',
        status: 'success',
        bottom: ['140px', '140px', '60px'],
      });
    }
  };

  return (
    <Box w="100%" height="auto" display="flex" flexDirection="column" position="relative">
      {uiLoaded && <TikTokPixel />}
      {uiLoaded && <MatomoTrackingScript />}
      {uiLoaded && <GTM gtmId="GTM-TS49J6SS" />}
      {uiLoaded && <FacebookPixel />}

      {/* {!location.pathname.includes('viewer') && ( */}
      {uiLoaded && !location.pathname.includes('viewer') && (
        <Image
          src={TopOverlay}
          w={['100%']}
          height={['160px', '170px', '200px', '230px', '280px']}
          objectFit="cover"
          objectPosition="center"
          position="fixed"
          zIndex="85"
          alt="top gradient overlay"
          loading="eager"
        />
      )}

      {uiLoaded && !location.pathname.includes('viewer') && (
        <Navbar
          cartItems={cartItems}
          setCartActive={(state) => setCartActive(state)}
          openWishlist={() => setWishlistActive(true)}
        />
      )}

      {uiLoaded && (
        <Box display="flex" flexDirection="column" transition="0.2s all" height={['auto']} zIndex="90">
          <Box
            width={['100%']}
            height={[bannerClosed ? '100dvh' : 'calc(100dvh - 70px)']}
            overflow="auto"
            display="flex"
            flexDirection="column"
            transition={'all 0.3s'}
            sx={{
              height: bannerClosed ? `${height}px` : `calc(${height}px - 70px)`,
              '@supports (height: 100dvh)': {
                height: bannerClosed ? '100dvh' : 'calc(100dvh - 70px)',
              },
            }}
          >
            {children}
          </Box>
          <Box
            bg="white"
            as="footer"
            position="fixed"
            bottom="0"
            left="0"
            zIndex="200"
            width="100%"
            display="flex"
            flexDirection={['column', 'column', 'row']}
            flexWrap="wrap"
            justifyContent="center"
            alignItems="center"
            gap={[1, 1, 6]}
            pointerEvents={bannerClosed ? 'none' : 'auto'}
            sx={{
              transition: 'opacity 0.3s, transform 0.3s',
              opacity: bannerClosed ? 0 : 1,
              transform: bannerClosed ? 'translateY(100%)' : 'translateY(0)',
              height: ['70px'],
            }}
          >
            <Text color="rgba(97, 97, 97, 1)" fontSize={['10px', '10px', '12px']} fontFamily="EverydaySans-Regular-Web">
              © 2026 Walmart. All Rights Reserved.
            </Text>
            <Box display="flex" flexWrap="wrap" justifyContent="center" alignItems="center" gap={[4, 4, 6]}>
              <Button
                fontSize={['10px', '10px', '12px']}
                fontFamily="EverydaySans-Regular-Web"
                fontWeight="100"
                textDecor="none"
                variant="link"
                color="rgba(97, 97, 97, 1)"
                onClick={() =>
                  window.open(
                    'https://www.walmart.com/help/article/walmart-com-terms-of-use/3b75080af40340d6bbd596f116fae5a0',
                    '_blank',
                  )
                }
              >
                Terms of Use
              </Button>

              <Button
                fontSize={['10px', '10px', '12px']}
                fontFamily="EverydaySans-Regular-Web"
                fontWeight="100"
                textDecor="none"
                variant="link"
                color="rgba(97, 97, 97, 1)"
                onClick={() =>
                  window.open('https://corporate.walmart.com/privacy-security/walmart-privacy-notice', '_blank')
                }
              >
                Privacy Notice
              </Button>
            </Box>

            <CloseIcon
              color="black"
              boxSize={[4]}
              right={['15px', '15px', '30px']}
              position="absolute"
              cursor="pointer"
              onClick={() => setBannerClosed(true)}
            />
          </Box>
        </Box>
      )}

      {/* {uiLoaded && (
        <Box
          zIndex="200"
          opacity={!bannerClosed ? 1 : 0}
          visibility={!bannerClosed ? 'visible' : 'hidden'}
          transition="all 0.2s"
          position="fixed"
          bottom="0px"
          bg="white"
          width="100%"
          height={['70px']}
          display="flex"
          flexDirection={['column', 'column', 'row']}
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          gap={[1, 1, 6]}
        >
          <Text color="rgba(97, 97, 97, 1)" fontSize={['10px', '10px', '12px']} fontFamily="EverydaySans-Regular-Web">
            © 2024 Walmart. All Rights Reserved.
          </Text>
          <Box display="flex" flexWrap="wrap" justifyContent="center" alignItems="center" gap={[4, 4, 6]}>
            <Button
              fontSize={['10px', '10px', '12px']}
              fontFamily="EverydaySans-Regular-Web"
              fontWeight="100"
              textDecor="none"
              variant="link"
              color="rgba(97, 97, 97, 1)"
              onClick={() =>
                window.open(
                  'https://www.walmart.com/help/article/walmart-com-terms-of-use/3b75080af40340d6bbd596f116fae5a0',
                  '_blank',
                )
              }
            >
              Terms of Use
            </Button>

            <Button
              fontSize={['10px', '10px', '12px']}
              fontFamily="EverydaySans-Regular-Web"
              fontWeight="100"
              textDecor="none"
              variant="link"
              color="rgba(97, 97, 97, 1)"
              onClick={() =>
                window.open('https://corporate.walmart.com/privacy-security/walmart-privacy-notice', '_blank')
              }
            >
              Privacy Notice
            </Button>
          </Box>

          <CloseIcon
            color="black"
            boxSize={[4]}
            right={['15px', '15px', '30px']}
            position="absolute"
            cursor="pointer"
            onClick={() => setBannerClosed(true)}
          />
        </Box>
      )} */}

      <Box
        opacity={showSplashScreen ? 1 : 0}
        visibility={showSplashScreen ? 'visible' : 'hidden'}
        transition={showSplashScreen ? 'unset' : 'visibility 0s linear 0.5s, opacity 0.5s'}
        className="loading"
        height="100vh"
        width="100vw"
        zIndex="500"
        position="fixed"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        margin="0"
        bg="red"
      >
        <Box className="svg-container" width="100%" height="100%" position="relative" overflow="hidden">
          <Box
            display={['flex', 'none', 'none', 'none', 'none']}
            alignItems="center"
            overflow="hidden"
            height="100%"
            width="100%"
          >
            <WalmartRealmAnimationMobile />
          </Box>

          <Box
            display={['none', 'flex', 'none', 'none', 'none']}
            alignItems="center"
            overflow="hidden"
            height="100%"
            width="100%"
          >
            <WalmartRealmAnimationTablet />
          </Box>

          <Box
            display={['none', 'none', 'flex', 'none', 'none']}
            alignItems="center"
            overflow="hidden"
            height="100%"
            width="100%"
          >
            <WalmartRealmAnimationSmallerDesktop />
          </Box>

          <Box
            display={['none', 'none', 'none', 'flex', 'none']}
            alignItems="center"
            overflow="hidden"
            height="100%"
            width="100%"
          >
            <WalmartRealmAnimationDesktop />
          </Box>

          <Box
            display={['none', 'none', 'none', 'none', 'flex']}
            alignItems="center"
            overflow="hidden"
            height="100%"
            width="100%"
          >
            <WalmartRealmAnimationBiggerDesktop />
          </Box>
        </Box>
      </Box>

      {uiLoaded && (
        <CartDrawer
          data={cartItems}
          active={cartActive}
          close={() => setCartActive(false)}
          setCartItems={setCartItems}
          setWishlistItems={setWishlistItems}
          openWishlist={() => {
            setCartActive(false);
            setWishlistActive(true);
          }}
        />
      )}

      {uiLoaded && (
        <WishlistDrawer
          data={wishlistItems}
          active={wishlistActive}
          close={() => setWishlistActive(false)}
          setCartItems={setCartItems}
          setWishlistItems={setWishlistItems}
          shareWishlist={handleShareWishlist}
        />
      )}

      {/* {productDrawerLoading && (
        <Spinner
          position="fixed"
          top="0px"
          left="0px"
          bottom="0px"
          right="0px"
          margin="auto"
          thickness="4px"
          speed="0.2s"
          color="white"
          size="xl"
          zIndex="9999"
        />
      )} */}

      {/* <ArViewer
        pId={productDrawerData.productId}
        productDrawerData={productDrawerData.data}
        active={arViewerActive}
        setCartItems={setCartItems}
        setWishlistItems={setWishlistItems}
        close={() => {
          setArViewerActive(false);
          setProductIdTrail([]);
          setProductIdTrailStartedId('');
          setProductDrawerData({ ...productDrawerData, active: false });
        }}
        openCart={() => {
          setArViewerActive(false);
          setProductDrawerData({ ...productDrawerData, active: false });
          setCartActive(true);
        }}
        openPDP={() => {
          setArViewerActive(false);
          setProductDrawerData({ ...productDrawerData, active: true });
        }}
      /> */}

      {/* <WMProductDrawer
        productId={productDrawerData.productId}
        productDrawerData={productDrawerData.data}
        productIdTrail={productIdTrail}
        active={productDrawerData.active}
        close={() => {
          setCartActive(false);
          setProductDrawerData({ ...productDrawerData, active: false });
        }}
        openCart={() => {
          setCartActive(true);
          setProductDrawerData({ ...productDrawerData, active: false });
        }}
        setCartItems={setCartItems}
        setWishlistItems={setWishlistItems}
        openProductModal={(variantId, fromRecommendation, addToTrail, removeFromTrail) =>
          openProductModal(variantId, '', fromRecommendation, addToTrail, removeFromTrail)
        }
        showOutOfStockToast={() => {
          showToast({
            id: 'outOfStock',
            title: 'Sorry, this item is currently sold out. Check back later for a restock.',
            status: 'info',
          });
        }}
        activateArViewer={() => {
          setProductDrawerData({ ...productDrawerData, active: false });
          activateARViewer(productDrawerData.productId);
        }}
        fromRecommendation={productDrawerData.fromRecommendation}
      /> */}
    </Box>
  );
}

export default Overlay;
